import { createSelector } from 'reselect';
import { STRINGS } from 'config';
import _map from 'lodash/map';
import _every from 'lodash/every';
import _includes from 'lodash/includes';
import _trim from 'lodash/trim';
import _compact from 'lodash/compact';
import _join from 'lodash/join';
import _split from 'lodash/split';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import { isEqual, isAfter, formatDistance, isBefore, isDate } from 'date-fns';

const { PRODUCT: { PRIMARY_CARE, E_SPECIALIST } } = STRINGS;

const isAllSelected = (multiSelectedAnswers, answers) => {
  const multiSelectedAnswersIds = _map(multiSelectedAnswers, answer => answer.answerId);
  const answerIdsToCheck = _map(answers, answer => answer.answerId);
  return _every(answerIdsToCheck, id => _includes(multiSelectedAnswersIds, id));
};

export const getClinicalTemplateVersionFromCondition = (templates, conditionCodeName) => {
  const clinicalTemplate = _find(templates, template => {
    return _includes(template.referredId, conditionCodeName);
  });
  if (!clinicalTemplate) {
    return null;
  }
  return clinicalTemplate.version;
};

const protectiveTrim = item => (item ? _trim(item) : '');

const concatArray = array => {
  const arrayWithFalseyValuesRemoved = _compact(array);
  return _join(arrayWithFalseyValuesRemoved, ', ');
};

const formatAPIAddressesResults = addresses => {
  return _map(addresses, address => {
    const addressArray = _split(address, ',');
    const isSixthItem = protectiveTrim(addressArray[5]);
    const addressObject = {
      line1: protectiveTrim(addressArray[0]),
      line2: protectiveTrim(addressArray[1]),
      line3: concatArray([protectiveTrim(addressArray[2]), protectiveTrim(addressArray[3]), (isSixthItem ? protectiveTrim(addressArray[4]) : '')]),
      city: isSixthItem || protectiveTrim(addressArray[4]),
    };
    return { ...addressObject, displayName: concatArray([addressObject.line1, addressObject.line2, addressObject.line3]) };
  });
};

export const consultationSelector = createSelector(
  state => state.consultationReducer,
  state => state.sessionReducer,
  state => state.flagsReducer,
  state => state.loginReducer,
  (consultationState, sessionState, flagsState, loginState) => {
    const consultationInProgress = consultationState.responseType === STRINGS.RESPONSE_TYPE.QUESTION
      || consultationState.responseType === STRINGS.RESPONSE_TYPE.QUESTION_SUICIDAL_CONFIRMATION;
    const isConsultationEnded = _includes(consultationState.responseType, 'ENDSTATE');
    const isAllMultipleInvalid = _get(consultationState, 'question.type', null) === STRINGS.QUESTION_TYPE.ALLMULTIPLE
      && !isAllSelected(consultationState.multiSelectedAnswers, _get(consultationState, 'question.answers', null));
    const isSessionExpiryTimeEmpty = _isEmpty(consultationState.sessionExpiryTime) && !isDate(consultationState.sessionExpiryTime);
    const isSessionExpired = isEqual(new Date(consultationState.sessionExpiryTime), new Date()) || isBefore(new Date(consultationState.sessionExpiryTime), new Date());
    const isSessionActive = consultationState
      && (isSessionExpiryTimeEmpty || isEqual(new Date(consultationState.sessionExpiryTime), new Date()) || isAfter(new Date(consultationState.sessionExpiryTime), new Date()));
    const consultationStartTime = (!_isEmpty(consultationState.consultationStartTime) || isDate(consultationState.consultationStartTime))
      && formatDistance(new Date(consultationState.consultationStartTime), new Date());
    const isNavigatedToPage = window.performance.navigation.type === 0;
    const isResumingConsultation = consultationStartTime
      && isNavigatedToPage
      && consultationInProgress
      && !consultationState.isContinueAsGuest
      && !consultationState.isInternalRedirectBlockResume
      && (consultationState.product === PRIMARY_CARE || consultationState.product === E_SPECIALIST);
    const validationErrors = [...consultationState.validationErrors, ...sessionState.validationErrors];
    const addresses = formatAPIAddressesResults(consultationState.addressesAPIResults);
    const isCompoundQuestion = _get(consultationState, 'question.type', null) === STRINGS.QUESTION_TYPE.COMPOUND;
    const addressesCity = _get(addresses, '[0].city', null);
    const isLoginQuestion = _get(consultationState, 'question.type', null) === STRINGS.QUESTION_TYPE.LOGIN;
    const isPreviousBlocked = isLoginQuestion && consultationState.loginDetails.isLoggedIn;
    const clinicalReviewConsultHeader = (consultationState.treatmentCategoryDetails.name && consultationState.treatmentCategoryDetails.version) ? (
      `${consultationState.treatmentCategoryDetails.name} - ${consultationState.treatmentCategoryDetails.version}`
    ) : (
      consultationState.conditionName || consultationState.treatmentCategory
    );
    const isPopupSurveyAvailableForProduct = consultationState.product !== STRINGS.PRODUCT.E_TRIAGE;
    const consultationId = _split(consultationState.sessionId, '-')[0];
    const isLoginConsentRejected = loginState.error === STRINGS.NHS_CONSENT_ERROR && loginState.error_description === STRINGS.NHS_CONSENT_ERROR_DESCRIPTION;
    const photoAttachmentInProgress = _find(
      consultationState.photos[consultationState.question.questionId],
      photo => photo.optimisticAdd || photo.optimisticDelete,
    ) !== undefined;
    return {
      ...consultationState,
      isCancelProgressModalOpen: sessionState.isCancelProgressModalOpen,
      consultationInProgress: consultationInProgress,
      isConsultationEnded: isConsultationEnded,
      isButtonsDisabled: consultationState.isLoading || photoAttachmentInProgress,
      isAllMultipleInvalid: isAllMultipleInvalid,
      isSessionActive: isSessionActive,
      isNavigatedToPage: isNavigatedToPage,
      isResumingConsultation: isResumingConsultation,
      consultationStartTime: consultationStartTime,
      isSessionExpiryTimeEmpty: isSessionExpiryTimeEmpty,
      isSessionExpired: isSessionExpired,
      validationErrors: validationErrors,
      addresses: addresses,
      isClinicalReviewMode: flagsState.isClinicalReviewMode,
      isCompoundQuestion: isCompoundQuestion,
      addressesCity: addressesCity,
      isLoginQuestion: isLoginQuestion,
      isPreviousBlocked: isPreviousBlocked,
      validationMessages: sessionState.validationMessages,
      clinicalReviewConsultHeader: clinicalReviewConsultHeader,
      isPopupSurveyAvailableForProduct: isPopupSurveyAvailableForProduct,
      isLoginConsentRejected: isLoginConsentRejected,
      consultationId: consultationId,
      isETriageBackHidden: flagsState.isETriageBackHidden,
      photoAttachmentInProgress: photoAttachmentInProgress,
    };
  },
);
